import { Typography } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import Tooltip from '@material-ui/core/Tooltip'
import BarChartIcon from '@material-ui/icons/BarChartOutlined'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import MoneyIcon from '@material-ui/icons/MoneyOutlined'
import ReviewOffIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'
import ReviewOnIcon from '@material-ui/icons/UpdateOutlined'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { BiBarChart, BiDownload, BiImage, BiInfoCircle, BiLineChart, BiToggleLeft, BiToggleRight } from 'react-icons/bi'

import InformationBanner from './InformationBanner'

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    card: {
      flex: '1',
      minWidth: 400,
      margin: theme.spacing(),
      padding: theme.spacing(),
      background: 'white',
      boxShadow: 'none',
    },
    cardTitle: {
      fontSize: '15px',
    },
    avatar: { color: 'white' },
    actions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      color: theme.palette.secondary.main,
    },
    bannerMessage: {
      marginTop: theme.spacing(4),
      marginRight: theme.spacing(5),
      marginLeft: theme.spacing(5),
    },
    content: {
      paddingTop: theme.spacing(4),
      paddingBottom: 0,
      backgroundColor: 'white',
      borderRadius: '5px',
      minHeight: 300,
    },
    icon: {
      minWidth: 35,
    },
    iconButton: {
      background: '#ECF5FF',
      borderRadius: '4px',
      height: '36px',
      width: '36px',
      padding: 0,
      color: theme.palette.primary.main,
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    progress: {
      margin: theme.spacing(2),
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    cardHeader: {
      justifyContent: 'center',
    },
  })

export interface IToggleIcon {
  active: boolean
  activeIcon: string
  activeText: string
  inactiveIcon: string
  inactiveText: string
  onToggle: () => void
}

interface IProps extends WithStyles<typeof styles> {
  bannerMessage?: string
  children: React.ReactNode
  description?: string
  disableImageExport?: boolean
  info?: string
  loading?: boolean
  onExport?: () => void
  onImageExport?: () => void
  subheader?: string
  title?: string
  toggles?: IToggleIcon[]
}

const getIcon = (icon: string) => {
  switch (icon) {
    case 'toggle-off':
      return <BiToggleLeft />
    case 'toggle-on':
      return <BiToggleRight />
    case 'percent':
      return <MoneyIcon />
    case 'bar':
      return <BiBarChart />
    case 'line':
      return <BiLineChart />
    case 'review-on':
      return <ReviewOnIcon />
    case 'review-off':
      return <ReviewOffIcon />
    default:
      return null
  }
}

const ChartContainer = (props: IProps) => {
  const {
    classes,
    children,
    title = '',
    subheader = '',
    onExport,
    onImageExport,
    info,
    loading = false,
    description,
    toggles,
    bannerMessage,
    disableImageExport = false,
  } = props
  const { t } = useTranslation()

  function displayTooltipText(text: string) {
    return (
      <Typography variant="body1" color="inherit">
        {text}
      </Typography>
    )
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        avatar={<BarChartIcon />}
        className={classes.cardHeader}
        action={
          <div className={classes.container}>
            {toggles &&
              toggles.map((toggleIcon, idx: number) => (
                <Tooltip
                  title={displayTooltipText(toggleIcon.active ? toggleIcon.activeText : toggleIcon.inactiveText)}
                  key={idx}
                >
                  <IconButton className={classes.iconButton} aria-label="tooltip" onClick={toggleIcon.onToggle}>
                    {toggleIcon.active ? getIcon(toggleIcon.activeIcon) : getIcon(toggleIcon.inactiveIcon)}
                  </IconButton>
                </Tooltip>
              ))}
            {description && (
              <Tooltip title={displayTooltipText(description)}>
                <IconButton className={classes.iconButton} aria-label="tooltip">
                  <BiInfoCircle />
                </IconButton>
              </Tooltip>
            )}
            {onExport && (
              <Tooltip title={displayTooltipText(t('Export as CSV'))}>
                <IconButton
                  className={classes.iconButton}
                  aria-label="tooltip"
                  onClick={onExport}
                  data-testid="export-csv-button"
                >
                  <BiDownload />
                </IconButton>
              </Tooltip>
            )}
            {!disableImageExport && onImageExport && (
              <Tooltip className="btn-export-image" title={displayTooltipText(t('Export as image'))}>
                <IconButton className={classes.iconButton} aria-label="tooltip" onClick={onImageExport}>
                  <BiImage />
                </IconButton>
              </Tooltip>
            )}
          </div>
        }
        titleTypographyProps={{
          className: classes.title,
        }}
        title={title}
        subheader={subheader}
      />
      <Divider />
      {bannerMessage && (
        <InformationBanner showBanner={true} message={bannerMessage} className={classes.bannerMessage} />
      )}
      <CardContent
        className={classNames(classes.content, {
          [classes.progressContainer]: loading,
        })}
      >
        {loading ? <CircularProgress className={classes.progress} /> : children}
      </CardContent>
      <CardActions className={classes.actions} disableSpacing={true}>
        {info && <InfoIcon />}
      </CardActions>
    </Card>
  )
}

export default withStyles(styles)(ChartContainer)
